// Catálogo: tipos de equipo (eqptt)
import { createApi } from '@reduxjs/toolkit/query/react';
import dynamicBaseQueryWithLogger from '@/services/utils/dynamicBaseQueryWithLogger';
import { handlerErrorResponse, handleUnwrapRTKError } from '@/services/utils/handlers';
import { setOrderStatuses, resetOrderStatuses } from '@/features/dashboard/settings/order-states/orderStatusesSlice';
import { setAppNotice } from '@/app/store/appShellSlice';

const BASE_URL = process.env.NEXT_PUBLIC_REPAROLY_API_URL ?? 'https://api.reparoly.local';

export interface ApiResponseBase<T> {
  success: boolean;
  message: string;
  data: T;
}

export type EquipmentTypeItem = {
  id: number;
  name: string;
};

export type EquipmentBrandItem = {
  id: number;
  name: string;
};

export type EquipmentModelItem = {
  id: number;
  name: string;
  equipbrand_id: number;
};

type EquipmentTypeListData = {
  items: EquipmentTypeItem[];
};

type CreateEquipmentTypeInput = {
  name: string;
};

type CreateEquipmentTypeResult = {
  id: number;
};

export const catalogApi = createApi({
  reducerPath: 'catalogApi',
  baseQuery: dynamicBaseQueryWithLogger(BASE_URL),
  tagTypes: [
    'EquipmentTypes',
    'EquipmentBrands',
    'EquipmentModels',
    'EquipmentModelsByBrand',
    'Warranties',
    'OrderStatuses',
  ],
  endpoints: (builder) => ({
    // GET /v1/catalog/os (admin, con enabled + sort_order)
    getOrderStatusesAdmin: builder.query<ApiResponseBase<{ items: OrderStatusRecord[] }>, void>({
      query: () => ({ url: '/v1/catalog/os', method: 'GET' }),
      transformErrorResponse: handlerErrorResponse('Catalog.OsAdmin'),
      providesTags: ['OrderStatuses'],
      keepUnusedDataFor: 0,
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al listar estados (admin)',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Catalog.OsAdmin'}${e?.code ? ` — Código: ${e.code}` : ''}`,
              messageFormatted: '',
              action: 'catalog.os.admin.list',
              endpointName: e?.endpointName || 'Catalog.OsAdmin',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),
    // PUT /v1/warranties/{id}/active
    updateWarrantyActive: builder.mutation<
      ApiResponseBase<{ id: number; is_active: boolean }> | ApiResponseBase<unknown>,
      { id: number | string; is_active: boolean }
    >({
      query: ({ id, is_active }) => ({
        url: `/v1/warranties/${id}/active`,
        method: 'PUT',
        body: { is_active },
      }),
      transformErrorResponse: handlerErrorResponse('Warranties.Active'),
      invalidatesTags: ['Warranties'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al actualizar estatus de la garantía',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Warranties.Active'}${e?.code ? ` — Código: ${e.code}` : ''}`,
              messageFormatted: '',
              action: 'warranties.active',
              endpointName: e?.endpointName || 'Warranties.Active',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),
    // GET /v1/catalog/os-list (Order Status list)
    getOrderStatuses: builder.query<ApiResponseBase<{ items: OrderStatusItem[] }>, void>({
      query: () => ({ url: '/v1/catalog/os-list', method: 'GET' }),
      transformErrorResponse: handlerErrorResponse('Catalog.OsList'),
      providesTags: ['OrderStatuses'],
      keepUnusedDataFor: 0, // Remove immediately when leaving the page
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const res = await queryFulfilled;
          try {
            const items = (res as any)?.data?.data?.items || [];
            // Map a OrderStatusRecord shape for the shared slice
            const mapped = items.map((it: OrderStatusItem, idx: number) => ({
              id: it.id,
              status_id: it.status_id,
              name: it.name,
              enabled: true,
              sort_order: idx,
            })) as OrderStatusRecord[];
            dispatch(setOrderStatuses({ items: mapped }));
          } catch {}
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al listar estados de orden',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Catalog.OsList'}${e?.code ? ` — Código: ${e.code}` : ''}`,
              messageFormatted: '',
              action: 'catalog.os.list',
              endpointName: e?.endpointName || 'Catalog.OsList',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),
    // GET /v1/catalog/eqptt-list
    getEquipmentTypes: builder.query<ApiResponseBase<EquipmentTypeListData>, void>({
      query: () => ({ url: '/v1/catalog/eqptt-list', method: 'GET' }),
      transformErrorResponse: handlerErrorResponse('Catalog.EqpttList'),
      providesTags: ['EquipmentTypes'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al listar tipos de equipo',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Catalog.EqpttList'}${e?.code ? ` — Código: ${e.code}` : ''}`,
              messageFormatted: '',
              action: 'catalog.eqptt.list',
              endpointName: e?.endpointName || 'Catalog.EqpttList',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),

    // GET /v1/catalog/eqptm-list (todos los modelos)
    getEquipmentModels: builder.query<ApiResponseBase<{ items: EquipmentModelItem[] }>, void>({
      query: () => ({ url: '/v1/catalog/eqptm-list', method: 'GET' }),
      transformErrorResponse: handlerErrorResponse('Catalog.EqptmList'),
      providesTags: ['EquipmentModels'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al listar modelos',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Catalog.EqptmList'}${e?.code ? ` — Código: ${e.code}` : ''}`,
              messageFormatted: '',
              action: 'catalog.eqptm.list',
              endpointName: e?.endpointName || 'Catalog.EqptmList',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),

    // GET /v1/catalog/eqptb-list (marcas)
    getEquipmentBrands: builder.query<ApiResponseBase<{ items: EquipmentBrandItem[] }>, void>({
      query: () => ({ url: '/v1/catalog/eqptb-list', method: 'GET' }),
      transformErrorResponse: handlerErrorResponse('Catalog.EqptbList'),
      providesTags: ['EquipmentBrands'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al listar marcas',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Catalog.EqptbList'}${e?.code ? ` — Código: ${e.code}` : ''}`,
              messageFormatted: '',
              action: 'catalog.eqptb.list',
              endpointName: e?.endpointName || 'Catalog.EqptbList',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),

    // POST /v1/catalog/eqptb-register (registrar marca)
    registerEquipmentBrand: builder.mutation<ApiResponseBase<{ id: number }>, { name: string }>({
      query: (body) => ({ url: '/v1/catalog/eqptb-register', method: 'POST', body }),
      transformErrorResponse: handlerErrorResponse('Catalog.EqptbRegister'),
      invalidatesTags: ['EquipmentBrands'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al registrar marca',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Catalog.EqptbRegister'}${e?.code ? ` — Código: ${e.code}` : ''}`,
              messageFormatted: '',
              action: 'catalog.eqptb.register',
              endpointName: e?.endpointName || 'Catalog.EqptbRegister',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),

    // GET /v1/catalog/eqptb/{brandId}/models
    getEquipmentModelsByBrand: builder.query<
      ApiResponseBase<{ items: EquipmentModelItem[] }>,
      number | string
    >({
      query: (brandId) => ({ url: `/v1/catalog/eqptb/${brandId}/models`, method: 'GET' }),
      transformErrorResponse: handlerErrorResponse('Catalog.EqptbModels'),
      providesTags: (_res, _err, brandId) => [
        { type: 'EquipmentModelsByBrand', id: String(brandId) },
      ],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al listar modelos',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Catalog.EqptbModels'}${e?.code ? ` — Código: ${e.code}` : ''}`,
              messageFormatted: '',
              action: 'catalog.eqptm.listByBrand',
              endpointName: e?.endpointName || 'Catalog.EqptbModels',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),

    // POST /v1/catalog/model-by-eqptbrand
    registerModelByBrand: builder.mutation<
      ApiResponseBase<{ id: number }>,
      { equipbrand_id: number; name: string }
    >({
      query: (body) => ({ url: '/v1/catalog/model-by-eqptbrand', method: 'POST', body }),
      transformErrorResponse: handlerErrorResponse('Catalog.ModelByEqptBrandRegister'),
      invalidatesTags: (_res, _err, arg) => [
        { type: 'EquipmentModelsByBrand', id: String(arg.equipbrand_id) },
      ],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al registrar modelo',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Catalog.ModelByEqptBrandRegister'}${
                e?.code ? ` — Código: ${e.code}` : ''
              }`,
              messageFormatted: '',
              action: 'catalog.eqptm.registerByBrand',
              endpointName: e?.endpointName || 'Catalog.ModelByEqptBrandRegister',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),

    // POST /v1/catalog/eqptt-register
    registerEquipmentType: builder.mutation<
      ApiResponseBase<CreateEquipmentTypeResult>,
      CreateEquipmentTypeInput
    >({
      query: (body) => ({ url: '/v1/catalog/eqptt-register', method: 'POST', body }),
      transformErrorResponse: handlerErrorResponse('Catalog.EqpttRegister'),
      invalidatesTags: ['EquipmentTypes'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al registrar tipo de equipo',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Catalog.EqpttRegister'}${
                e?.code ? ` — Código: ${e.code}` : ''
              }`,
              messageFormatted: '',
              action: 'catalog.eqptt.register',
              endpointName: e?.endpointName || 'Catalog.EqpttRegister',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),

    // GET /v1/warranties
    getWarranties: builder.query<ApiResponseBase<{ items: WarrantyItem[] }>, void>({
      query: () => ({ url: '/v1/warranties', method: 'GET' }),
      transformErrorResponse: handlerErrorResponse('Warranties.List'),
      providesTags: ['Warranties'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al listar garantías',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Warranties.List'}${e?.code ? ` — Código: ${e.code}` : ''}`,
              messageFormatted: '',
              action: 'warranties.list',
              endpointName: e?.endpointName || 'Warranties.List',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),
    // POST /v1/warranties
    registerWarranty: builder.mutation<
      ApiResponseBase<{ id: number }>,
      {
        code: string;
        name: string;
        duration_days: number;
        terms?: string | null;
        coverage?: Record<string, unknown> | null;
        is_active?: boolean;
      }
    >({
      query: (body) => ({ url: '/v1/warranties', method: 'POST', body }),
      transformErrorResponse: handlerErrorResponse('Warranties.Create'),
      invalidatesTags: ['Warranties'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al registrar garantía',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Warranties.Create'}${e?.code ? ` — Código: ${e.code}` : ''}`,
              messageFormatted: '',
              action: 'warranties.create',
              endpointName: e?.endpointName || 'Warranties.Create',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),
    // PUT /v1/warranties/{id}
    updateWarranty: builder.mutation<
      ApiResponseBase<{ id?: number }> | ApiResponseBase<unknown>,
      {
        id: number | string;
        code?: string;
        name: string;
        duration_days: number;
        terms?: string | null;
        coverage?: Record<string, unknown> | null;
        is_active?: boolean;
      }
    >({
      query: ({ id, ...body }) => ({ url: `/v1/warranties/${id}`, method: 'PUT', body }),
      transformErrorResponse: handlerErrorResponse('Warranties.Update'),
      invalidatesTags: ['Warranties'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al actualizar garantía',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Warranties.Update'}${e?.code ? ` — Código: ${e.code}` : ''}`,
              messageFormatted: '',
              action: 'warranties.update',
              endpointName: e?.endpointName || 'Warranties.Update',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),
    // PUT /v1/catalog/os/{id}/enabled
    updateOrderStatusEnabled: builder.mutation<
      ApiResponseBase<{ id?: number; enabled?: boolean }> | ApiResponseBase<unknown>,
      { id: number | string; enabled: boolean }
    >({
      query: ({ id, enabled }) => ({
        url: `/v1/catalog/os/${id}/enabled`,
        method: 'PUT',
        body: { enabled },
      }),
      transformErrorResponse: handlerErrorResponse('Catalog.OsEnabledUpdate'),
      invalidatesTags: ['OrderStatuses'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Invalida cache local en slice para forzar recarga en próxima visita
          dispatch(resetOrderStatuses());
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al actualizar estado (activo/inactivo)',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Catalog.OsEnabledUpdate'}${
                e?.code ? ` — Código: ${e.code}` : ''
              }`,
              messageFormatted: '',
              action: 'catalog.os.enabled.update',
              endpointName: e?.endpointName || 'Catalog.OsEnabledUpdate',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),

    // DELETE /v1/catalog/eqptt/{id}
    deleteEquipmentType: builder.mutation<ApiResponseBase<{ id: number }> | ApiResponseBase<unknown>, number | string>({
      query: (id) => ({ url: `/v1/catalog/eqptt/${id}`, method: 'DELETE' }),
      transformErrorResponse: handlerErrorResponse('Catalog.EqpttDelete'),
      invalidatesTags: ['EquipmentTypes'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al eliminar tipo de equipo',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Catalog.EqpttDelete'}${e?.code ? ` — Código: ${e.code}` : ''}`,
              messageFormatted: '',
              action: 'catalog.eqptt.delete',
              endpointName: e?.endpointName || 'Catalog.EqpttDelete',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),

    // DELETE /v1/catalog/eqptb/{id}
    deleteEquipmentBrand: builder.mutation<ApiResponseBase<{ id: number }> | ApiResponseBase<unknown>, number | string>({
      query: (id) => ({ url: `/v1/catalog/eqptb/${id}`, method: 'DELETE' }),
      transformErrorResponse: handlerErrorResponse('Catalog.EqptbDelete'),
      invalidatesTags: ['EquipmentBrands'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al eliminar marca',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Catalog.EqptbDelete'}${e?.code ? ` — Código: ${e.code}` : ''}`,
              messageFormatted: '',
              action: 'catalog.eqptb.delete',
              endpointName: e?.endpointName || 'Catalog.EqptbDelete',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),

    // DELETE /v1/catalog/eqptm/{id}
    deleteEquipmentModel: builder.mutation<
      ApiResponseBase<{ id: number }> | ApiResponseBase<unknown>,
      { id: number | string; brandId?: number | string }
    >({
      query: ({ id }) => ({ url: `/v1/catalog/eqptm/${id}`, method: 'DELETE' }),
      transformErrorResponse: handlerErrorResponse('Catalog.EqptmDelete'),
      invalidatesTags: (_res, _err, arg) => [
        'EquipmentModels',
        arg.brandId ? { type: 'EquipmentModelsByBrand', id: String(arg.brandId) } : { type: 'EquipmentModelsByBrand', id: 'unknown' },
      ],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al eliminar modelo',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Catalog.EqptmDelete'}${e?.code ? ` — Código: ${e.code}` : ''}`,
              messageFormatted: '',
              action: 'catalog.eqptm.delete',
              endpointName: e?.endpointName || 'Catalog.EqptmDelete',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),
  }),
});

export type WarrantyItem = {
  id: number;
  name: string;
  duration_days: number;
  company_id?: number;
  terms?: string | null;
};

export type OrderStatusItem = {
  id: number;
  status_id: number;
  name: string;
};

export type OrderStatusRecord = {
  id: number;
  status_id: number;
  name: string;
  enabled: boolean;
  sort_order: number;
};

export const {
  useGetEquipmentTypesQuery,
  useRegisterEquipmentTypeMutation,
  useGetEquipmentModelsQuery,
  useGetEquipmentBrandsQuery,
  useRegisterEquipmentBrandMutation,
  useGetEquipmentModelsByBrandQuery,
  useRegisterModelByBrandMutation,
  useDeleteEquipmentTypeMutation,
  useDeleteEquipmentBrandMutation,
  useDeleteEquipmentModelMutation,
  useGetWarrantiesQuery,
  useRegisterWarrantyMutation,
  useUpdateWarrantyMutation,
  useUpdateWarrantyActiveMutation,
  useGetOrderStatusesQuery,
  useLazyGetOrderStatusesQuery,
  useGetOrderStatusesAdminQuery,
  useUpdateOrderStatusEnabledMutation,
} = catalogApi;
